import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 2 * 60 * 1000; // check every 2 minutes
const VERSION_URL = '/version.json';

export function useVersionCheck() {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const currentVersionRef = useRef<string | null>(null);

    const fetchVersion = async (): Promise<string | null> => {
        try {
            const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
                cache: 'no-store',
                headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
            });
            if (!res.ok) return null;
            const data = await res.json();
            return data.version ?? null;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        // Record the version that was loaded when the app first started
        fetchVersion().then(v => {
            if (v) currentVersionRef.current = v;
        });

        const interval = setInterval(async () => {
            const latest = await fetchVersion();
            if (
                latest &&
                currentVersionRef.current &&
                latest !== currentVersionRef.current
            ) {
                setUpdateAvailable(true);
            }
        }, POLL_INTERVAL_MS);

        return () => clearInterval(interval);
    }, []);

    const applyUpdate = () => {
        // 1. Clear all caches (Cache API)
        if ('caches' in window) {
            caches.keys().then(names => names.forEach(name => caches.delete(name)));
        }

        // 2. Clear cookies
        document.cookie.split(';').forEach(cookie => {
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
            document.cookie = `${name.trim()}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });

        // 3. Clear storage
        try { localStorage.clear(); } catch (_) {}
        try { sessionStorage.clear(); } catch (_) {}

        // 4. Unregister service workers
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
                registrations.forEach(r => r.unregister());
            });
        }

        // 5. Hard reload — bypass all browser caches
        window.location.reload();
    };

    return { updateAvailable, applyUpdate };
}
